import { useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    aCreatedLinkAttachment,
    aIsAttachmentCreationModalOpen,
    aLinkAttachmentItemId,
    aLinkAttachmentItemIsPinned,
    aLinkAttachmentModalBox,
    aLinkAttachmentModalValue
} from './atom';
import { Buffer } from 'buffer';
import { getAuthHeader } from 'services/client-api-wrapper/services/auth_header';
import { useCurrentRoomId } from 'shared-state/location/hook';
import { useLocalProfile } from 'shared-state/identity/hooks';
import { getApiGatewayUrl } from 'kits/urlKit';

export interface ILinkAttachmentModalBoxState {
    toggleLinkAttachmentModalBox: boolean;
    cancelLinkAttachment: () => void;
    saveLinkAttachment: () => void;
    onLinkAttachmentValueChange: (e: any, newValue: any) => void;
    onLinkAttachmentPinnedChange: (e: any, newValue: any) => void;
    linkAttachmentModalValue: string;
    linkAttachmentItemId: string;
    linkAttachmentItemIsPinned: boolean;
}

export const useLinkAttachmentModalBoxState: () => ILinkAttachmentModalBoxState = () => {
    const [toggleLinkAttachmentModalBox, setToggleLinkAttachmentModalBox] = useRecoilState<boolean>(aLinkAttachmentModalBox);
    const [linkAttachmentModalValue, setLinkAttachmentModalValue] = useRecoilState(aLinkAttachmentModalValue);
    const [, setCreatedLinkAttachment] = useRecoilState(aCreatedLinkAttachment);
    const linkAttachmentItemId = useRecoilValue(aLinkAttachmentItemId);
    const [linkAttachmentItemIsPinned, setLinkAttachmentItemIsPinned] = useRecoilState(aLinkAttachmentItemIsPinned);
    const [, setIsAttachmentCreationModalOpen] = useRecoilState(aIsAttachmentCreationModalOpen);

    const [roomId] = useCurrentRoomId();
    const localProfile = useLocalProfile();
    const fetchNewConceptBoard = useCallback(async () => {
        const corsProxyAddress = getApiGatewayUrl('cors', 'default');
        // const corsProxyAddress = 'http://localhost:3007';
        const obj = {
            method: 'GET',
            headers: getAuthHeader()
        };
        console.log(`${corsProxyAddress}/getNewConceptBoard?roomId=${roomId}&userId=${localProfile?.uid}`, obj);

        try {
            const response = await fetch(`${corsProxyAddress}/getNewConceptBoard?roomId=${roomId}&userId=${localProfile?.uid}`, {
                method: 'GET',
                headers: getAuthHeader()
            });
            return await response.json();
        } catch (error) {
            console.error('Error fetching new conceptboard:', error);
        }
    }, []);

    const cancelLinkAttachment = () => {
        setIsAttachmentCreationModalOpen(false);
        setToggleLinkAttachmentModalBox(false);
        setCreatedLinkAttachment(false);
        setLinkAttachmentItemIsPinned(false);
    };

    const saveLinkAttachment = async () => {
        if (linkAttachmentModalValue.length > 0 && linkAttachmentItemId.endsWith('linkmanager')) {
            let urlToSave =
                linkAttachmentItemId === 'linkmanager' ? Buffer.from(linkAttachmentModalValue, 'binary').toString('base64') : linkAttachmentModalValue;
            console.log(urlToSave);
            console.log('!!! Saving link attachment:', urlToSave);
            console.log('!!! Link attachment item ID:', linkAttachmentItemId);
            if (linkAttachmentItemId === 'conceptboardlinkmanager' && linkAttachmentModalValue === 'https://conceptboard.com/new') {
                const response = await fetchNewConceptBoard();
                // console.log('!!!! fetchNewConceptBoard', response);
                if (response.error) {
                    console.log(`!!! Conceptboard error: ${response.error}`);
                    return;
                }
                urlToSave = response.location;
            }
            setLinkAttachmentModalValue(urlToSave);
            setCreatedLinkAttachment(true);
            setToggleLinkAttachmentModalBox(true);
        }
    };

    const onLinkAttachmentValueChange = (e: any, newValue: any) => {
        if (typeof newValue == 'string') {
            const httpTest = /https|http/;
            const p = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|(?:m\.)?youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/; // eslint-disable-line max-len
            const collaboardRegex = /^(?:https?:\/\/)(web|Api|de|ch-api|ch)\.collaboard\.app\/collaboard\/(\d+)$/;

            if (linkAttachmentItemId === 'youtubelinkmanager') {
                const matched = newValue.match(p);
                if (matched) setLinkAttachmentModalValue(matched[1]);
                else setLinkAttachmentModalValue('');
            } else if (linkAttachmentItemId === 'collaboardlinkmanager') {
                const matched = newValue.match(collaboardRegex);
                if (matched && parseInt(matched[2])) setLinkAttachmentModalValue(matched[2]);
                else setLinkAttachmentModalValue('');
            } else if (linkAttachmentItemId === 'conceptboardlinkmanager') {
                const conceptboardRegex = /^https:\/\/([^.]+(?:\.[^.]+)*?)\.conceptboard\.com\/board\/([a-zA-Z0-9-]+)$/;
                const matched = newValue.match(conceptboardRegex);
                // const isNewBoard = 'https://ivicos-eu.de.conceptboard.com/new' === newValue;
                const isNewBoard = 'https://conceptboard.com/new' === newValue;
                setLinkAttachmentItemIsPinned(!isNewBoard);

                if (matched || isNewBoard) {
                    // console.log('Conceptboard subdomain:', matched[1]);
                    // console.log('Conceptboard board ID:', matched[2]);
                    setLinkAttachmentModalValue(newValue);
                    // } else if (isNewBoard) {
                    // console.log('!!! fetchNewConceptBoard:', newValue);
                    // setLinkAttachmentModalValue(newValue);
                } else setLinkAttachmentModalValue('');
            } else if (httpTest.test(newValue)) {
                setLinkAttachmentModalValue(newValue);
            } else {
                const newUpdatedValue = `https://${newValue}`;
                setLinkAttachmentModalValue(newUpdatedValue);
            }
        } else {
            return;
        }
    };

    const onLinkAttachmentPinnedChange = (e: any, checked: any) => {
        if (typeof checked == 'boolean') {
            setLinkAttachmentItemIsPinned(checked);
        } else {
            return;
        }
    };

    return {
        toggleLinkAttachmentModalBox,
        cancelLinkAttachment,
        saveLinkAttachment,
        onLinkAttachmentValueChange,
        onLinkAttachmentPinnedChange,
        linkAttachmentModalValue,
        linkAttachmentItemId,
        linkAttachmentItemIsPinned
    };
};
